// SidebarControls component - left sidebar with tools and modifiers
import type {
  SidebarControlsConfig,
  SidebarControlsAPI,
} from "../../../../../../types/stage-types.js";
import {
  createFlowControlsInContainer,
  type FlowControlsAPI,
} from "./subcomponents/FlowControls/index.js";
import {
  createUndoRedoControlsInContainer,
  type UndoRedoControlsAPI,
} from "./subcomponents/UndoRedoControls/index.js";
import {
  createModifiersControlsInContainer,
  type ModifiersControlsAPI,
} from "./subcomponents/ModifiersControls/index.js";
import {
  createToolsControlsInContainer,
  type ToolsControlsAPI,
  type ToolMode,
} from "./subcomponents/ToolsControls/index.js";
import {
  getDefaultSubtoolForMode,
  getModeForPrimaryTool,
} from "@/data/tool-mode-map";
import type { SidebarVisibilityConfig } from "@/components/Stage/subcomponents/UIControlsLayer/visibility-profiles";
import type { HandednessStateAPI } from "@/state/handedness";

/**
 * Creates the SidebarControls component with flow, tools, and modifiers sections
 * @param container - Container element for the SidebarControls
 * @param config - Configuration with event handlers
 * @returns SidebarControls API
 */
export const createSidebarControls = (
  container: HTMLElement,
  config: SidebarControlsConfig = {}
): SidebarControlsAPI => {
  const { uiState, eventBus } = config;
  let isDestroyed = false;
  let currentTool = config.initialTool ?? "select";
  let activeModifiers = new Set<string>();
  let flowControlsAPI: FlowControlsAPI | null = null;
  let undoRedoControlsAPI: UndoRedoControlsAPI | null = null;
  let modifiersControlsAPI: ModifiersControlsAPI | null = null;
  let toolsControlsAPI: ToolsControlsAPI | null = null;
  let unsubscribeToolChanged: (() => void) | null = null;
  let rootElement: HTMLElement | null = null;
  let flowContainerEl: HTMLElement | null = null;
  let toolsContainerEl: HTMLElement | null = null;
  let undoRedoContainerEl: HTMLElement | null = null;
  let modifiersContainerEl: HTMLElement | null = null;
  let unsubscribeHandedness: (() => void) | null = null;
  let unsubscribeToolsLevelRequest: (() => void) | null = null;
  let currentToolsLevel: "main" | "sub" = "main";

  const handednessState: HandednessStateAPI | null =
    (config.handedness as HandednessStateAPI | null) ?? null;

  let currentHandedness: "left" | "right" =
    (handednessState?.get?.() as "left" | "right" | undefined) ?? "right";

  const applyHandednessClass = (value: "left" | "right") => {
    currentHandedness = value;
    if (!rootElement) return;
    rootElement.dataset.handedness = value;
    rootElement.classList.toggle("is-left-handed", value === "left");
    rootElement.classList.toggle("is-right-handed", value !== "left");
  };

  const emitToolSettingsRequest = (
    mode: string,
    subtool: string,
    anchor?: HTMLElement | null
  ): void => {
    config.onToolSettingsRequest?.(mode, subtool, anchor);
  };

  const handleToolSettingsRequest = (mode: ToolMode, subtool: string): void => {
    config.onToolSettingsRequest?.(mode, subtool);
  };

  /**
   * Initialize SidebarControls HTML structure
   */
  const initSidebarControls = (): void => {
    container.innerHTML = `
      <div class="sidebar-controls">
        <!-- flow: 44px width x 106px height -->
        <div class="flow-container"></div>
        
        <!-- tools: 76px width x flex-grow:1 height -->
        <div class="tools-container">
          <div class="tools-controls-container"></div>
          <div class="undo-redo-container"></div>
        </div>
        
        <!-- modifiers container: vertical flex -->
        <div class="modifiers-container">
          <!-- modifiers: 44px width x 123px height -->
          <div class="modifiers-container-inner"></div>
        </div>
      </div>
    `;

    rootElement = container.querySelector(
      ".sidebar-controls"
    ) as HTMLElement;
    applyHandednessClass(currentHandedness);

    // Initialize FlowControls component
    const flowContainer = container.querySelector(
      ".flow-container"
    ) as HTMLElement;
    flowContainerEl = flowContainer;
    if (flowContainer) {
      try {
        flowControlsAPI = createFlowControlsInContainer(flowContainer, {
          onPreviewClick: () => console.log("Preview clicked"),
          onAnnotateClick: () => console.log("Annotate clicked"),
        });
      } catch (error) {
        console.error("Failed to create FlowControls component:", error);
      }
    }

    // Initialize UndoRedoControls component
    const undoRedoContainer = container.querySelector(
      ".undo-redo-container"
    ) as HTMLElement;
    undoRedoContainerEl = undoRedoContainer;
    if (undoRedoContainer) {
      try {
        undoRedoControlsAPI = createUndoRedoControlsInContainer(
          undoRedoContainer,
          {
            onUndoClick: () => {
              console.log("Undo clicked");
              config.onUndo?.();
            },
            onRedoClick: () => {
              console.log("Redo clicked");
              config.onRedo?.();
            },
          }
        );
      } catch (error) {
        console.error("Failed to create UndoRedoControls component:", error);
      }
    }

    // Initialize ModifiersControls component
    const modifiersContainer = container.querySelector(
      ".modifiers-container-inner"
    ) as HTMLElement;
    modifiersContainerEl = modifiersContainer;
    if (modifiersContainer) {
      try {
        modifiersControlsAPI = createModifiersControlsInContainer(
          modifiersContainer,
          {
            onShiftToggle: (active) => {
              console.log("Shift toggle:", active);
              if (active) {
                activeModifiers.add("shift");
              } else {
                activeModifiers.delete("shift");
              }

              // Update UI state with modifier status
              uiState?.setShiftPressed(active);

              config.onModifierChange?.("shift", active);
            },
            onAltToggle: (active) => {
              console.log("Alt toggle:", active);
              if (active) {
                activeModifiers.add("alt");
              } else {
                activeModifiers.delete("alt");
              }

              // Update UI state with modifier status
              uiState?.setAltPressed(active);

              config.onModifierChange?.("alt", active);
            },
          }
        );
      } catch (error) {
        console.error("Failed to create ModifiersControls component:", error);
      }
    }

    // Initialize ToolsControls component
    const toolsContainer = container.querySelector(
      ".tools-container"
    ) as HTMLElement;
    const toolsControlsHost = container.querySelector(
      ".tools-controls-container"
    ) as HTMLElement;
    toolsContainerEl = toolsContainer;
    if (toolsControlsHost) {
      try {
        const st = (config as any)?.uiState?.getState?.();
        const initialMode =
          (st?.selectedMode as any) ||
          ((st?.selected_tool === "draw"
            ? "sketch"
            : st?.selected_tool) as any) ||
          ("edit" as any);
        const initialTool =
          (st?.selectedTool as any) ||
          (st?.selected_subtool as any) ||
          undefined;
        const initialEraserSubtool =
          st?.sketchEraserMode === "delete-clean" ? "delete-clean" : "delete";
        const initialSelectionSubtool =
          st?.sketchSelectionMode === "lasso" ? "lasso" : "marquee";

        if (undoRedoContainerEl) {
          undoRedoContainerEl.style.display = "none";
        }

        toolsControlsAPI = createToolsControlsInContainer(toolsControlsHost, {
          onModeChange: (mode) => {
            console.log("Tools: Mode changed to", mode);
            try {
              (config as any)?.uiState?.setMode(mode as any);
            } catch {}
          },
          onLevelChange: (level) => {
            if (undoRedoContainerEl) {
              undoRedoContainerEl.style.display =
                level === "main" ? "none" : "";
            }
            currentToolsLevel = level;
            config.eventBus?.emit("tools-level-changed", {
              level,
              mode: toolsControlsAPI?.getMode?.() ?? null,
            });
          },
          onToolSelect: (_mode, subtool) => {
            try {
              (config as any)?.uiState?.setToolSub(subtool as any);
            } catch {}
          },
          onToolSettingsRequest: (mode, subtool, anchor) => {
            emitToolSettingsRequest(mode as any, subtool, anchor);
          },
          initialMode: initialMode as any,
          initialTool: initialTool as any,
          initialEraserSubtool,
          initialSelectionSubtool,
        });

        if (eventBus?.on) {
          try {
            unsubscribeToolChanged = eventBus.on(
              "tool-changed",
              ({
                tool,
                subtool,
                mode,
              }: {
                tool: string;
                subtool: string | null;
                mode?: string | null;
              }) => {
                if (isDestroyed || !toolsControlsAPI) return;
                const resolvedMode =
                  (typeof mode === "string" && mode) ||
                  getModeForPrimaryTool(tool as any) ||
                  ("edit" as any);
                const nextSubtool =
                  typeof subtool === "string" ? subtool : undefined;
                try {
                  toolsControlsAPI.setModeAndTool(
                    resolvedMode as any,
                    nextSubtool
                  );
                } catch {}
                if (typeof tool === "string" && tool) {
                  currentTool = tool;
                }
              }
            );
          } catch {}
        }
      } catch (error) {
        console.error("Failed to create ToolsControls component:", error);
      }
    }
  };

  /**
   * Setup event listeners (none needed for placeholder boxes)
   */
  const setupEventListeners = (): void => {
    // No events needed for placeholder boxes
  };

  /**
   * Set active tool and update UI
   * @param tool - Tool name to activate
   */
  const setActiveTool = (tool: string): void => {
    if (isDestroyed) return;

    currentTool = tool;

    try {
      const mode = getModeForPrimaryTool(tool as any) || ("edit" as any);
      const state = uiState?.getState?.();
      const stateSubtool =
        (state?.selectedTool as string | undefined) ||
        (state?.selected_subtool as string | undefined) ||
        undefined;
      const fallbackSubtool =
        stateSubtool || getDefaultSubtoolForMode(mode as any) || undefined;

      if (
        (mode === "sketch" || mode === "edit") &&
        typeof fallbackSubtool === "string"
      ) {
        toolsControlsAPI?.setModeAndTool(mode as any, fallbackSubtool);
      } else {
        toolsControlsAPI?.setMode(mode as any);
      }
    } catch {}

    // Update button states
    const toolButtons = container.querySelectorAll(
      "[data-tool]"
    ) as NodeListOf<HTMLButtonElement>;
    toolButtons.forEach((btn) => {
      const btnTool = btn.getAttribute("data-tool");
      if (btnTool === tool) {
        btn.classList.add("tool-btn--active");
      } else {
        btn.classList.remove("tool-btn--active");
      }
    });
  };

  /**
   * Toggle modifier key state
   * @param modifier - Modifier key to toggle
   */
  const toggleModifier = (modifier: string): void => {
    if (isDestroyed) return;

    if (activeModifiers.has(modifier)) {
      activeModifiers.delete(modifier);
    } else {
      activeModifiers.add(modifier);
    }

    // Update button visual state
    const modifierBtn = container.querySelector(
      `#modifier-${modifier}`
    ) as HTMLButtonElement;
    if (modifierBtn) {
      if (activeModifiers.has(modifier)) {
        modifierBtn.classList.add("modifier-btn--active");
      } else {
        modifierBtn.classList.remove("modifier-btn--active");
      }
    }

    // Notify config of modifier change
    config.onModifierChange?.(modifier, activeModifiers.has(modifier));
  };

  /**
   * Get currently active modifiers
   * @returns Array of active modifier names
   */
  const getActiveModifiers = (): string[] => {
    return Array.from(activeModifiers);
  };

  /**
   * Get current tool
   * @returns Current tool name
   */
  const getCurrentTool = (): string => {
    return currentTool;
  };

  const setUndoDisabled = (disabled: boolean): void => {
    undoRedoControlsAPI?.setUndoDisabled(disabled);
  };

  const setRedoDisabled = (disabled: boolean): void => {
    undoRedoControlsAPI?.setRedoDisabled(disabled);
  };

  /**
   * Set component visibility
   * @param visible - Visibility state
   */
  const applyVisibility = (visibility: SidebarVisibilityConfig): void => {
    if (isDestroyed) return;

    const sidebarVisible = visibility.visible !== false;
    if (rootElement) {
      rootElement.style.display = sidebarVisible ? "" : "none";
    }
    container.style.display = sidebarVisible ? "" : "none";

    if (flowControlsAPI) {
      flowControlsAPI.setPreviewVisible(visibility.flow.preview.visible);
      if (visibility.flow.preview.icon) {
        flowControlsAPI.setPreviewIcon(visibility.flow.preview.icon);
      }
      flowControlsAPI.setAnnotateVisible(visibility.flow.annotate.visible);
      if (visibility.flow.annotate.icon) {
        flowControlsAPI.setAnnotateIcon(visibility.flow.annotate.icon);
      }
    }

    if (flowContainerEl) {
      const showFlow =
        visibility.flow.preview.visible || visibility.flow.annotate.visible;
      flowContainerEl.style.display = showFlow ? "" : "none";
    }

    if (toolsContainerEl) {
      toolsContainerEl.style.display = visibility.tools.visible ? "" : "none";
    }

    if (modifiersContainerEl) {
      const wrapper = modifiersContainerEl.parentElement as HTMLElement | null;
      if (wrapper) {
        wrapper.style.display = visibility.modifiers.visible ? "" : "none";
      }
    }
  };

  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Clean up components
    flowControlsAPI?.destroy();
    flowControlsAPI = null;

    undoRedoControlsAPI?.destroy();
    undoRedoControlsAPI = null;

    modifiersControlsAPI?.destroy();
    modifiersControlsAPI = null;

    toolsControlsAPI?.destroy();
    toolsControlsAPI = null;

    try {
      unsubscribeToolChanged?.();
    } catch {}
    unsubscribeToolChanged = null;

    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }
    if (unsubscribeToolsLevelRequest) {
      try {
        unsubscribeToolsLevelRequest();
      } catch {}
      unsubscribeToolsLevelRequest = null;
    }

    container.innerHTML = "";
  };

  // Initialize component
  initSidebarControls();
  if (handednessState && typeof handednessState.subscribe === "function") {
    unsubscribeHandedness = handednessState.subscribe((value) => {
      if (value === "left" || value === "right") {
        applyHandednessClass(value);
      }
    });
  }
  if (eventBus?.on) {
    unsubscribeToolsLevelRequest = eventBus.on(
      "request-tools-level",
      () => {
        eventBus.emit("tools-level-changed", {
          level: currentToolsLevel,
          mode: toolsControlsAPI?.getMode?.() ?? null,
        });
      }
    );
  }
  // No initial tool/subtool forcing
  setupEventListeners();

  // Return API
  return {
    setActiveTool,
    toggleModifier,
    getActiveModifiers,
    getCurrentTool,
    setUndoDisabled,
    setRedoDisabled,
    applyVisibility,
    setVisible,
    destroy,
  };
};
